import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { BookOpen, Award, Users, X } from 'lucide-react';

export default function Education() {
  const { t, language } = useLanguage();
  const [lightboxImage, setLightboxImage] = useState(null);

  const assets = {
    petitPatriote: 'https://static.prod-images.emergentagent.com/jobs/7cf829f0-16ef-4383-a637-469dccd1e538/images/978e3eee130409c8599a9809fcd06bec2fa0c558698e35c43bc14e3b0fbe42ed.png',
    educationHero: 'https://images.pexels.com/photos/8923253/pexels-photo-8923253.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  };

  // Couvertures des manuels scolaires "Le Petit Patriote"
  const textbookCovers = [
    { id: 'ce1', name: 'CE1', url: 'https://customer-assets.emergentagent.com/job_sustainable-africa-4/artifacts/8zju4xcy_CE1.png' },
    { id: 'ce2', name: 'CE2', url: 'https://customer-assets.emergentagent.com/job_sustainable-africa-4/artifacts/pff07ksk_CE2.png' },
    { id: 'cm1', name: 'CM1', url: 'https://customer-assets.emergentagent.com/job_sustainable-africa-4/artifacts/fcmdlat5_CM1.png' },
    { id: 'cm2', name: 'CM2', url: 'https://customer-assets.emergentagent.com/job_sustainable-africa-4/artifacts/ykzsrd4j_CM2.png' },
    { id: 'class3', name: 'CLASS 3', url: 'https://customer-assets.emergentagent.com/job_sustainable-africa-4/artifacts/ca39tiy4_CLASS%203.png' },
    { id: 'class4', name: 'CLASS 4', url: 'https://customer-assets.emergentagent.com/job_sustainable-africa-4/artifacts/gliz04d0_CLASS%204.png' },
    { id: 'class5', name: 'CLASS 5', url: 'https://customer-assets.emergentagent.com/job_sustainable-africa-4/artifacts/ejdpvl3s_CLASS%205.png' },
    { id: 'class6', name: 'CLASS 6', url: 'https://customer-assets.emergentagent.com/job_sustainable-africa-4/artifacts/p6veex5c_CLASS%206.png' },
  ];

  // Contenu multilingue pour la section des manuels
  const textbooksContent = {
    fr: {
      title: 'Collection Complète',
      subtitle: 'Manuels Scolaires "Le Petit Patriote"',
      description: 'Une collection pédagogique complète couvrant du CE1 au CM2 pour le système francophone et de CLASS 3 à CLASS 6 pour le système anglophone.',
      francophone: 'Système Francophone',
      anglophone: 'Système Anglophone',
      clickToEnlarge: 'Cliquer pour agrandir'
    },
    en: {
      title: 'Complete Collection',
      subtitle: '"Le Petit Patriote" Textbooks',
      description: 'A complete educational collection covering CE1 to CM2 for the French-speaking system and CLASS 3 to CLASS 6 for the English-speaking system.',
      francophone: 'French-Speaking System',
      anglophone: 'English-Speaking System',
      clickToEnlarge: 'Click to enlarge'
    },
    pt: {
      title: 'Coleção Completa',
      subtitle: 'Manuais Escolares "Le Petit Patriote"',
      description: 'Uma coleção pedagógica completa que cobre do CE1 ao CM2 para o sistema francófono e da CLASS 3 à CLASS 6 para o sistema anglófono.',
      francophone: 'Sistema Francófono',
      anglophone: 'Sistema Anglófono',
      clickToEnlarge: 'Clique para ampliar'
    },
    it: {
      title: 'Collezione Completa',
      subtitle: 'Manuali Scolastici "Le Petit Patriote"',
      description: 'Una collezione pedagogica completa che copre dal CE1 al CM2 per il sistema francofono e dalla CLASS 3 alla CLASS 6 per il sistema anglofono.',
      francophone: 'Sistema Francofono',
      anglophone: 'Sistema Anglofono',
      clickToEnlarge: 'Clicca per ingrandire'
    },
    ru: {
      title: 'Полная коллекция',
      subtitle: 'Учебники "Le Petit Patriote"',
      description: 'Полная педагогическая коллекция, охватывающая от CE1 до CM2 для франкоязычной системы и от CLASS 3 до CLASS 6 для англоязычной системы.',
      francophone: 'Франкоязычная система',
      anglophone: 'Англоязычная система',
      clickToEnlarge: 'Нажмите для увеличения'
    },
    zh: {
      title: '完整系列',
      subtitle: '"小爱国者"教科书',
      description: '完整的教育系列，涵盖法语系统的CE1至CM2和英语系统的CLASS 3至CLASS 6。',
      francophone: '法语系统',
      anglophone: '英语系统',
      clickToEnlarge: '点击放大'
    }
  };

  const tc = textbooksContent[language] || textbooksContent.fr;

  return (
    <div className="min-h-screen bg-white pt-32">
      <section className="py-24 md:py-32 bg-gradient-to-br from-slate-50 to-white" data-testid="education-hero">
        <div className="max-w-6xl mx-auto px-6">
          <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6">
            {t('education.title')}
          </h1>
          <p className="text-base md:text-lg text-slate-600 leading-relaxed max-w-3xl">
            {t('education.subtitle')}
          </p>
        </div>
      </section>

      <section className="py-16" data-testid="petit-patriote-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-lg">
                <img src={assets.petitPatriote} alt="Le Petit Patriote" className="w-full" />
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="inline-block px-4 py-2 bg-sky-100 rounded-full mb-6">
                <p className="text-sm font-semibold text-sky-600 uppercase tracking-wider">
                  {t('education.petitPatriote.subtitle')}
                </p>
              </div>
              <h2 className="text-4xl md:text-5xl font-semibold text-slate-900 mb-6">
                {t('education.petitPatriote.title')}
              </h2>
              <p className="text-base text-slate-600 leading-relaxed mb-8">
                {t('education.petitPatriote.description')}
              </p>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Award className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      {t('education.petitPatriote.ministry')}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Users className="w-5 h-5 text-sky-600 flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      {t('education.petitPatriote.support')}
                    </p>
                    <p className="text-xs text-slate-600">
                      {t('education.petitPatriote.prince')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Textbook Covers Gallery */}
      <section className="py-20 bg-gradient-to-b from-sky-50 to-white" data-testid="textbooks-gallery">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-2 bg-sky-100 rounded-full mb-4">
              <p className="text-sm font-semibold text-sky-600 uppercase tracking-wider">
                {tc.title}
              </p>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Playfair Display, serif'}}>
              {tc.subtitle}
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              {tc.description}
            </p>
          </div>

          {/* Francophone System - CE1, CE2, CM1, CM2 */}
          <div className="mb-12">
            <h3 className="text-xl font-semibold text-slate-800 mb-6 flex items-center gap-2">
              <span className="w-3 h-3 bg-sky-500 rounded-full"></span>
              {tc.francophone}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {textbookCovers.slice(0, 4).map((book) => (
                <div 
                  key={book.id}
                  className="group cursor-pointer"
                  onClick={() => setLightboxImage(book)}
                  data-testid={`textbook-${book.id}`}
                >
                  <div className="relative rounded-xl overflow-hidden border border-slate-200 shadow-md hover:shadow-xl transition-all transform hover:scale-105">
                    <img 
                      src={book.url} 
                      alt={`Le Petit Patriote ${book.name}`}
                      className="w-full h-auto"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                      <span className="text-white font-medium opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 px-4 py-2 rounded-full text-sm">
                        {tc.clickToEnlarge}
                      </span>
                    </div>
                  </div>
                  <p className="text-center mt-3 font-semibold text-slate-700">{book.name}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Anglophone System - CLASS 3, 4, 5, 6 */}
          <div>
            <h3 className="text-xl font-semibold text-slate-800 mb-6 flex items-center gap-2">
              <span className="w-3 h-3 bg-emerald-500 rounded-full"></span>
              {tc.anglophone}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {textbookCovers.slice(4).map((book) => (
                <div 
                  key={book.id}
                  className="group cursor-pointer"
                  onClick={() => setLightboxImage(book)}
                  data-testid={`textbook-${book.id}`}
                >
                  <div className="relative rounded-xl overflow-hidden border border-slate-200 shadow-md hover:shadow-xl transition-all transform hover:scale-105">
                    <img 
                      src={book.url} 
                      alt={`Le Petit Patriote ${book.name}`}
                      className="w-full h-auto"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                      <span className="text-white font-medium opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 px-4 py-2 rounded-full text-sm">
                        {tc.clickToEnlarge}
                      </span>
                    </div>
                  </div>
                  <p className="text-center mt-3 font-semibold text-slate-700">{book.name}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-900 text-white" data-testid="vision-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block px-4 py-2 bg-white/10 rounded-full mb-6">
              <p className="text-sm font-semibold uppercase tracking-wider">
                {t('education.vision.title')}
              </p>
            </div>
            <p className="text-base md:text-lg leading-relaxed">
              {t('education.vision.description')}
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="bg-white border border-slate-200 rounded-3xl p-12">
            <div className="text-center max-w-3xl mx-auto">
              <BookOpen className="w-16 h-16 text-sky-600 mx-auto mb-6" />
              <h3 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                {language === 'fr' && 'Formation du Capital Humain'}
                {language === 'en' && 'Human Capital Formation'}
                {language === 'pt' && 'Formação do Capital Humano'}
                {language === 'it' && 'Formazione del Capitale Umano'}
                {language === 'ru' && 'Формирование человеческого капитала'}
                {language === 'zh' && '人力资本培养'}
              </h3>
              <p className="text-base text-slate-600 leading-relaxed">
                {language === 'fr' && 'Le programme "Le Petit Patriote" forme les citoyens africains de demain, conscients de leur responsabilité dans la construction d\'un continent souverain et prospère.'}
                {language === 'en' && 'The "Le Petit Patriote" program trains tomorrow\'s African citizens, aware of their responsibility in building a sovereign and prosperous continent.'}
                {language === 'pt' && 'O programa "Le Petit Patriote" forma os cidadãos africanos de amanhã, conscientes da sua responsabilidade na construção de um continente soberano e próspero.'}
                {language === 'it' && 'Il programma "Le Petit Patriote" forma i cittadini africani di domani, consapevoli della loro responsabilità nella costruzione di un continente sovrano e prospero.'}
                {language === 'ru' && 'Программа "Le Petit Patriote" формирует граждан Африки завтрашнего дня, осознающих свою ответственность в построении суверенного и процветающего континента.'}
                {language === 'zh' && '"小爱国者"项目培养明天的非洲公民，让他们意识到自己在建设主权繁荣大陆中的责任。'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
          data-testid="textbook-lightbox"
        >
          <button 
            className="absolute top-6 right-6 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors"
            onClick={() => setLightboxImage(null)}
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <div className="text-center">
            <img 
              src={lightboxImage.url}
              alt={`Le Petit Patriote ${lightboxImage.name}`}
              className="max-w-full max-h-[85vh] rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <p className="text-white text-xl font-semibold mt-4">{lightboxImage.name}</p>
          </div>
        </div>
      )}
    </div>
  );
}
